import React, { createContext, useState, useEffect, useMemo, useContext } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import themes from "./themes"; // mapa: { scaniaLight: {...}, scaniaDark: {...}, ... }
import { AuthContext } from "./AuthContext"; // <- usa o userData.tema do usuário logado
import { readTabUserData } from "../utils/tabAuthStorage";

const ThemeContext = createContext();

const DEFAULT_THEME = "claro";
const LEGACY_STORAGE_KEY = "currentTheme";

function themeStorageKey(userId) {
  return userId ? `${LEGACY_STORAGE_KEY}:${userId}` : LEGACY_STORAGE_KEY;
}

function readPersistedTheme(userId) {
  if (userId) {
    const perUser = localStorage.getItem(themeStorageKey(userId));
    if (perUser && themes[perUser]) return perUser;
  }
  const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
  if (!legacy || !themes[legacy]) return null;
  // chave legada sem escolha explícita por usuário: ignora default auto-gravado antigo
  if (userId && (legacy === DEFAULT_THEME || legacy === "scaniaLight")) return null;
  return legacy;
}

function writePersistedTheme(userId, themeKey) {
  if (userId) {
    localStorage.setItem(themeStorageKey(userId), themeKey);
  }
  localStorage.setItem(LEGACY_STORAGE_KEY, themeKey);
}

function readInitialUserId() {
  try {
    const id = Number(readTabUserData()?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

const ThemeContextProvider = ({ children }) => {
  const { userData } = useContext(AuthContext);

  // estado inicial: preferência local do usuário; senão default até o perfil carregar
  const [currentTheme, setCurrentTheme] = useState(() => {
    return readPersistedTheme(readInitialUserId()) || DEFAULT_THEME;
  });

  // perfil só define o tema quando ainda não há preferência local (ex.: primeiro acesso)
  useEffect(() => {
    const profileTheme = userData?.tema;
    if (!profileTheme || !themes[profileTheme]) return;

    const saved = readPersistedTheme(userData?.id);
    if (saved) {
      if (saved !== currentTheme) setCurrentTheme(saved);
      return;
    }

    if (profileTheme !== currentTheme) {
      setCurrentTheme(profileTheme);
    }
  }, [userData?.id, userData?.tema]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeTheme = (themeKey) => {
    if (themes[themeKey]) {
      setCurrentTheme(themeKey);
      writePersistedTheme(userData?.id ?? readInitialUserId(), themeKey);
    } else {
      console.warn(`Tema ${themeKey} não encontrado.`);
    }
  };

  const muiTheme = useMemo(() => {
    const base = themes[currentTheme];
    const placeholderColor = base.palette?.input?.placeholder;
    const inputTextColor = base.palette?.input?.text;

    return createTheme({
      ...base,
      components: {
        ...base.components,
        MuiInputBase: {
          styleOverrides: {
            ...(inputTextColor
              ? {
                  root: {
                    color: inputTextColor,
                  },
                }
              : {}),
            ...(placeholderColor
              ? {
                  input: {
                    "&::placeholder": {
                      color: placeholderColor,
                      opacity: 1,
                    },
                  },
                }
              : {}),
            ...base.components?.MuiInputBase?.styleOverrides,
          },
        },
      },
    });
  }, [currentTheme]);

  return (
    <ThemeContext.Provider value={{ currentTheme, changeTheme }}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => useContext(ThemeContext);

/** Tema MUI persistido (localStorage), para telemetria fora de componentes React. */
export function getPersistedAppThemeKey(userId) {
  const uid = userId != null ? userId : readInitialUserId();
  const saved = readPersistedTheme(uid);
  if (saved && themes[saved]) return saved;
  return themes[DEFAULT_THEME] ? DEFAULT_THEME : null;
}

export default ThemeContextProvider;

/** @deprecated use useTranslatedThemeOptions() */
export const availableThemes = Object.keys(themes).map((key) => ({
  label: key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()),
  value: key,
}));
